import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook to manage monitor log filters
 * Handles status code and time period filtering with URL parameter generation
 * 
 * @param {Object} initialFilters - Initial filter values
 * @returns {Object} Filter state and methods
 */
const useMonitorFilters = (initialFilters = {}) => {
  const [filters, setFilters] = useState({
    statusCodes: {
      type: initialFilters?.statusCodes?.type || 'all',
      codes: initialFilters?.statusCodes?.codes || []
    },
    timePeriod: {
      type: initialFilters?.timePeriod?.type || 'all',
      startDate: initialFilters?.timePeriod?.startDate || null,
      endDate: initialFilters?.timePeriod?.endDate || null
    },
    service: initialFilters?.service || 'all'
  });

  /**
   * Update status code filter
   */
  const setStatusFilter = useCallback((type, codes = []) => {
    setFilters(prev => ({
      ...prev,
      statusCodes: { type, codes }
    }));
  }, []);

  /**
   * Update time period filter
   */
  const setTimeFilter = useCallback((type, startDate = null, endDate = null) => {
    setFilters(prev => ({
      ...prev,
      timePeriod: { type, startDate, endDate }
    }));
  }, []);

  /**
   * Update service filter
   */
  const setServiceFilter = useCallback((service) => {
    setFilters(prev => ({
      ...prev,
      service
    }));
  }, []);

  /**
   * Reset all filters to default (all)
   */
  const resetFilters = useCallback(() => {
    setFilters({
      statusCodes: { type: 'all', codes: [] },
      timePeriod: { type: 'all', startDate: null, endDate: null },
      service: 'all'
    });
  }, []);

  /**
   * Update multiple filters at once
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  /**
   * Check if any filters are currently active
   */
  const hasActiveFilters = useMemo(() => {
    return filters.statusCodes.type !== 'all' || filters.timePeriod.type !== 'all' || filters.service !== 'all';
  }, [filters]);

  /**
   * Count number of active filters
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.statusCodes.type !== 'all') count++;
    if (filters.timePeriod.type !== 'all') count++;
    if (filters.service !== 'all') count++;
    return count;
  }, [filters]);

  /**
   * Generate URL parameters for API call
   * @returns {URLSearchParams} URL parameters object
   */
  const getApiParams = useCallback((baseParams = {}) => {
    const params = new URLSearchParams();
    
    // Add base parameters
    Object.entries(baseParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    });

    // Add status filter if active
    if (filters.statusCodes.type !== 'all') {
      params.set('statusFilter', filters.statusCodes.type);
      
      if (filters.statusCodes.type === 'custom' && filters.statusCodes.codes.length > 0) {
        params.set('statusCodes', filters.statusCodes.codes.join(','));
      }
    }

    // Add time filter if active
    if (filters.timePeriod.type !== 'all') {
      if (filters.timePeriod.type === 'custom') {
        if (filters.timePeriod.startDate) {
          params.set('startDate', filters.timePeriod.startDate.toISOString());
        }
        if (filters.timePeriod.endDate) {
          params.set('endDate', filters.timePeriod.endDate.toISOString());
        }
      } else {
        params.set('timePeriod', filters.timePeriod.type);
      }
    }

    return params;
  }, [filters]);

  /**
   * Get a human-readable description of active filters
   */
  const getFilterDescription = useMemo(() => {
    const descriptions = [];
    
    if (filters.statusCodes.type !== 'all') {
      if (filters.statusCodes.type === 'custom') {
        descriptions.push(`Status: ${filters.statusCodes.codes.join(', ')}`);
      } else {
        const statusMap = {
          '2xx': 'Success (2xx)',
          '4xx': 'Client Errors (4xx)',
          '5xx': 'Server Errors (5xx)'
        };
        descriptions.push(`Status: ${statusMap[filters.statusCodes.type] || filters.statusCodes.type}`);
      }
    }
    
    if (filters.timePeriod.type !== 'all') {
      if (filters.timePeriod.type === 'custom') {
        const start = filters.timePeriod.startDate?.toLocaleDateString();
        const end = filters.timePeriod.endDate?.toLocaleDateString();
        descriptions.push(`Time: ${start} - ${end}`);
      } else {
        const periodMap = {
          '1h': 'Last 1 Hour',
          '6h': 'Last 6 Hours',
          '24h': 'Last 24 Hours'
        };
        descriptions.push(`Time: ${periodMap[filters.timePeriod.type] || filters.timePeriod.type}`);
      }
    }
    
    return descriptions.join(' • ');
  }, [filters]);

  return {
    filters,
    setStatusFilter,
    setTimeFilter,
    setServiceFilter,
    resetFilters,
    updateFilters,
    getApiParams,
    hasActiveFilters,
    activeFilterCount,
    getFilterDescription
  };
};

export default useMonitorFilters;